/**
 * QR ordering – Terms & Privacy (VOP + GDPR) template, SOURCE OF TRUTH for the generated text.
 *
 * The legal text lives here as an HTML string with {{placeholders}} and flat {{#if key}}…{{/if}}
 * blocks (no nesting — combined conditions are precomputed in buildContext). The customer fills the
 * variables in AdminUI › zákazník › „Obchodné podmienky", clicks „Vygenerovať", the text renders into
 * a WYSIWYG editor (editable), and on save the final HTML is shown to the guest in the app modal.
 *
 * Datavision (prevádzkovateľ systému) data and the notification provider (Brevo) are hardcoded here.
 * Language: Slovak only. One combined document (VOP, then a separator, then Ochrana osobných údajov).
 *
 * Do not change the legal wording without a legal review.
 */

// Datavision Systems s.r.o. — hardcoded operator identity (shown in both documents).
export const DATAVISION = {
  obchodneMeno: "Datavision Systems s.r.o.",
  sidlo: "Slnečná ulica 1911/1, 922 03 Vrbové",
  ico: "56760817",
  zapisOr: "Obchodnom registri Okresného súdu Trnava, oddiel Sro, vložka č. 58599/T",
  email: "info@datavision.sk",
  telefon: "+421 918 755 096",
  emailPodpora: "info@datavision.sk",
  emailGdpr: "info@datavision.sk",
  // Notification provider for order confirmations / status updates.
  notifikaciePoskytovatel: "Brevo (Sendinblue SAS, Francúzsko – EÚ)",
};

/**
 * Customer-filled fields. `required` fields gate the „Vygenerovať" button; `requiredIf` is a key that
 * must be truthy for the field to become required. `group` starts a visual group. `prefill` is the
 * key in the backend's `termsPrefill` map used to seed the field.
 */
export const TERMS_FIELDS = [
  { key: "prevadzka.obchodne_meno", group: "Údaje prevádzky", label: "Obchodné meno", type: "text", required: true, prefill: "prevadzka.obchodne_meno" },
  { key: "prevadzka.sidlo", label: "Sídlo", type: "text", required: true, prefill: "prevadzka.sidlo" },
  { key: "prevadzka.ico", label: "IČO", type: "text", required: true, prefill: "prevadzka.ico" },
  { key: "prevadzka.nazov_prevadzky", label: "Názov prevádzky", type: "text", required: true, prefill: "prevadzka.nazov_prevadzky" },
  { key: "prevadzka.adresa_prevadzky", label: "Adresa prevádzky", type: "text", required: true, prefill: "prevadzka.adresa_prevadzky" },
  { key: "prevadzka.email", label: "E-mail", type: "email", required: true, prefill: "prevadzka.email" },
  { key: "prevadzka.telefon", label: "Telefón", type: "text", required: true, prefill: "prevadzka.telefon" },
  { key: "prevadzka.zodpovedna_osoba", label: "Zodpovedná osoba (DPO) – nepovinné", type: "text", required: false, help: "Meno a kontakt zodpovednej osoby. Prázdne = nie je určená." },

  { key: "platba.online", group: "Platba", label: "Platba online", type: "bool", default: true },
  { key: "platba.pri_stole", label: "Platba u obsluhy", type: "bool", default: true },

  { key: "vydanie.na_stol", group: "Vydanie objednávky", label: "Na stôl", type: "bool", default: true },
  { key: "vydanie.vydajne_miesto", label: "Výdajné miesto", type: "bool", default: false },
  { key: "vydanie.dovoz", label: "Dovoz", type: "bool", default: false },
  { key: "vydanie.osobny_odber", label: "Osobný odber", type: "bool", default: false },

  { key: "gdpr.uchovavanie_objednavok_mesiace", group: "Uchovávanie údajov (GDPR)", label: "Objednávky (mesiace)", type: "number", default: 24, min: 1, required: true },
  { key: "gdpr.uchovavanie_logov_dni", label: "Technické logy (dni)", type: "number", default: 90, min: 1, required: true },

  { key: "doplnkove_podmienky", group: "Doplnkové podmienky prevádzky", label: "Doplnkové podmienky (bod 9 VOP) – nepovinné", type: "richtext", required: false },
];

/** Default variable values from the schema. */
export const termsDefaults = () => {
  const out = {};
  for (const f of TERMS_FIELDS) {
    if (f.type === "bool") out[f.key] = f.default === true;
    else if (f.type === "number") out[f.key] = f.default != null ? f.default : "";
    else out[f.key] = "";
  }
  return out;
};

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const isRichEmpty = (v) => !v || v === "<p><br></p>" || String(v).replace(/<[^>]*>/g, "").trim() === "";

/**
 * Are all required inputs filled so the text can be generated? Also enforces: at least one payment
 * type and at least one fulfillment mode (the template assumes at least one of each).
 * @returns {{ ok: boolean, missing: string[] }}
 */
export const termsComplete = (vars) => {
  const v = vars || {};
  const missing = [];
  for (const f of TERMS_FIELDS) {
    const required = f.required === true || (f.requiredIf && v[f.requiredIf] === true);
    if (!required) continue;
    const val = v[f.key];
    if (f.type === "number") {
      if (val === "" || val == null || !Number.isFinite(Number(val)) || Number(val) < (f.min ?? 0)) missing.push(f.label);
    } else if (f.type === "email") {
      if (!val || !EMAIL_RE.test(String(val).trim())) missing.push(f.label);
    } else if (f.type === "richtext") {
      if (isRichEmpty(val)) missing.push(f.label);
    } else if (!String(val || "").trim()) {
      missing.push(f.label);
    }
  }
  if (!(v["platba.online"] === true || v["platba.pri_stole"] === true)) missing.push("aspoň jeden spôsob platby");
  if (
    !(
      v["vydanie.na_stol"] === true ||
      v["vydanie.vydajne_miesto"] === true ||
      v["vydanie.dovoz"] === true ||
      v["vydanie.osobny_odber"] === true
    )
  )
    missing.push("aspoň jeden spôsob vydania");
  return { ok: missing.length === 0, missing };
};

const escapeHtml = (s) =>
  String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

/** Today's date in Slovak format DD.MM.RRRR. */
const todaySk = () => {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getDate())}.${p(d.getMonth() + 1)}.${d.getFullYear()}`;
};

/** Build the flat render context (dotted vars + precomputed derived booleans). */
const buildContext = (vars) => {
  const v = vars || {};
  const online = v["platba.online"] === true;
  const stul = v["platba.pri_stole"] === true;
  const naStol = v["vydanie.na_stol"] === true;
  const vydaj = v["vydanie.vydajne_miesto"] === true;
  const dovoz = v["vydanie.dovoz"] === true;
  const osobnyOdber = v["vydanie.osobny_odber"] === true;
  const dpo = !!String(v["prevadzka.zodpovedna_osoba"] || "").trim();
  const doplnkove = !isRichEmpty(v["doplnkove_podmienky"]);
  return {
    ...v,
    platne_od: todaySk(),
    d_online: online,
    d_onlineAndStul: online && stul,
    d_onlineOnly: online && !stul,
    d_stulOnly: stul && !online,
    d_naStol: naStol,
    d_vydaj: vydaj,
    d_dovoz: dovoz,
    d_osobnyOdber: osobnyOdber,
    d_doplnkove: doplnkove,
    d_dpo: dpo,
    d_noDpo: !dpo,
  };
};

/**
 * Apply flat {{#if}}/{{else}}/{{/if}} and {{var}} / {{{raw}}} substitutions (no nesting).
 */
const applyTemplate = (tpl, ctx) => {
  let out = tpl.replace(/\{\{#if\s+([\w.]+)\}\}([\s\S]*?)\{\{\/if\}\}/g, (_, key, inner) => {
    const parts = inner.split(/\{\{else\}\}/);
    return ctx[key] ? parts[0] : parts[1] || "";
  });
  out = out.replace(/\{\{\{\s*([\w.]+)\s*\}\}\}/g, (_, key) => (ctx[key] != null ? String(ctx[key]) : ""));
  out = out.replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_, key) => escapeHtml(ctx[key] != null ? String(ctx[key]) : ""));
  return out;
};

// The legal template (SK). Datavision data + Brevo are literal; only customer variables are {{…}}.
const TEMPLATE_HTML = `
<h2>Obchodné podmienky QR objednávania – {{prevadzka.nazov_prevadzky}}</h2>
<p>Platné od {{platne_od}}.</p>

<h3>1. Kto je kto</h3>
<ul>
<li><strong>Predávajúci:</strong> {{prevadzka.obchodne_meno}}, so sídlom {{prevadzka.sidlo}}, IČO {{prevadzka.ico}}, prevádzka {{prevadzka.nazov_prevadzky}}, {{prevadzka.adresa_prevadzky}}, e-mail {{prevadzka.email}}, tel. {{prevadzka.telefon}} (ďalej „prevádzka").</li>
<li><strong>Prevádzkovateľ systému:</strong> Datavision Systems s.r.o., so sídlom Slnečná ulica 1911/1, 922 03 Vrbové, IČO 56760817, zapísaná v Obchodnom registri Okresného súdu Trnava, oddiel Sro, vložka č. 58599/T, e-mail info@datavision.sk, tel. +421 918 755 096 (ďalej „Datavision").</li>
<li>Zmluvu o jedle a nápojoch uzatvárate s prevádzkou. Datavision nie je zmluvnou stranou, len technicky zabezpečuje objednávku{{#if d_online}} a online platbu{{/if}}. Za ponuku, ceny, kvalitu a vydanie objednávky zodpovedá prevádzka, za funkčnosť systému Datavision.</li>
</ul>

<h3>2. Objednávka</h3>
<ol>
<li>Naskenujete QR kód, vyberiete položky, vyplníte meno a telefón alebo e-mail{{#if d_onlineAndStul}} a zvolíte spôsob platby{{/if}}.</li>
<li>Objednávku odošlete tlačidlom „Objednávka s povinnosťou platby".</li>
<li>Zmluva vzniká potvrdením objednávky prevádzkou; potvrdenie sa zobrazí na obrazovke a pošle na zadaný kontakt.</li>
<li>Ak položka nie je dostupná, prevádzka vás bude kontaktovať alebo objednávku odmietne{{#if d_online}}; zaplatenú sumu vráti rovnakým spôsobom platby{{/if}}.</li>
</ol>

<h3>3. Cena a platba</h3>
<ul>
<li>Ceny v menu sú konečné, vrátane DPH.</li>
<li>{{#if d_onlineAndStul}}Platíte online (karta, Apple Pay, Google Pay) cez platobnú bránu Besteron (Besteron a.s.) alebo u obsluhy. Údaje o karte spracúva iba platobná brána.{{/if}}{{#if d_onlineOnly}}Platíte online (karta, Apple Pay, Google Pay) cez platobnú bránu Besteron (Besteron a.s.); objednávka sa odošle až po zaplatení. Údaje o karte spracúva iba platobná brána.{{/if}}{{#if d_stulOnly}}Platíte u obsluhy alebo pri pokladni.{{/if}}</li>
<li>Prevádzka vám vydá pokladničný doklad.</li>
</ul>

<h3>4. Vydanie objednávky</h3>
<p>Objednávku vám prevádzka odovzdá bez zbytočného odkladu po príprave jedným zo spôsobov, ktoré pri objednávke ponúka:</p>
<ul>
{{#if d_naStol}}<li>obsluha ju prinesie na stôl, z ktorého ste ju odoslali;</li>{{/if}}
{{#if d_vydaj}}<li>vyzdvihnete si ju na výdajnom mieste prevádzky;</li>{{/if}}
{{#if d_dovoz}}<li>prevádzka vám ju doručí (dovoz) na zadanú adresu v rámci rozvozovej oblasti; cena dovozu a dostupnosť závisia od zvolenej oblasti;</li>{{/if}}
{{#if d_osobnyOdber}}<li>vyzdvihnete si ju osobne na prevádzke (osobný odber); o pripravení objednávky vás prevádzka informuje.</li>{{/if}}
</ul>

<h3>5. Odstúpenie od zmluvy</h3>
<p>Od zmluvy nemôžete odstúpiť, lebo ide o pokrmy podliehajúce rýchlej skaze a o stravovacie služby poskytované v dohodnutom čase (§ 19 ods. 1 písm. d) a l) zákona č. 108/2024 Z. z.). Objednávku môžete zrušiť u obsluhy, kým sa nezačala pripravovať.</p>

<h3>6. Reklamácie</h3>
<ul>
<li>Vady jedla, nápojov alebo služby reklamujte hneď u obsluhy alebo e-mailom na {{prevadzka.email}}. Prevádzka zodpovedá za vady podľa Občianskeho zákonníka a reklamáciu vybaví bez zbytočného odkladu, najneskôr do 30 dní.</li>
<li>Technické problémy so systémom alebo platbou hláste Datavision na info@datavision.sk.</li>
</ul>

<h3>7. Riešenie sporov</h3>
<p>Ak nie ste spokojní s vybavením, požiadajte prevádzku o nápravu. Ak odmietne alebo neodpovie do 30 dní, môžete podať návrh na alternatívne riešenie sporu Slovenskej obchodnej inšpekcii (www.soi.sk) alebo inému subjektu zo zoznamu Ministerstva hospodárstva SR (zákon č. 391/2015 Z. z.). Dozor vykonáva SOI.</p>

<h3>8. Záverečné ustanovenia</h3>
<p>Platí slovenské právo. Spracúvanie osobných údajov upravuje dokument Ochrana osobných údajov nižšie. Prevádzka a Datavision môžu tieto podmienky zmeniť; pre objednávku platí znenie účinné pri jej odoslaní.</p>
{{#if d_doplnkove}}
<h3>9. Doplnkové podmienky prevádzky</h3>
{{{doplnkove_podmienky}}}
{{/if}}

<hr />

<h2>Ochrana osobných údajov – {{prevadzka.nazov_prevadzky}}</h2>
<p>Platné od {{platne_od}}. Informácie podľa čl. 13 nariadenia (EÚ) 2016/679 (GDPR) a zákona č. 18/2018 Z. z.</p>

<h3>1. Kto spracúva vaše údaje</h3>
<ul>
<li><strong>Prevádzkovateľ:</strong> {{prevadzka.obchodne_meno}}, {{prevadzka.sidlo}}, IČO {{prevadzka.ico}}, e-mail {{prevadzka.email}}. Rozhoduje o účele spracúvania a je kontaktom pre vaše práva.</li>
<li><strong>Sprostredkovateľ:</strong> Datavision Systems s.r.o., Slnečná ulica 1911/1, 922 03 Vrbové, IČO 56760817, e-mail info@datavision.sk, ktorá prevádzkuje QR objednávkový systém na základe zmluvy s prevádzkou (čl. 28 GDPR). Pre technické logy a bezpečnosť systému je Datavision samostatným prevádzkovateľom.</li>
<li>Zodpovedná osoba: {{#if d_dpo}}{{prevadzka.zodpovedna_osoba}}{{/if}}{{#if d_noDpo}}nie je určená{{/if}}.</li>
</ul>

<h3>2. Aké údaje, na čo a na akom základe</h3>
<ul>
<li><strong>Meno, telefón alebo e-mail, číslo stola, objednané položky, cena</strong> — Účel: prijatie, príprava a vydanie objednávky, oznamy o jej stave, reklamácie. Právny základ: plnenie zmluvy, čl. 6 ods. 1 písm. b) GDPR. Doba uchovávania: {{gdpr.uchovavanie_objednavok_mesiace}} mesiacov od objednávky.</li>
<li><strong>Doklad o platbe (bez údajov o karte)</strong> — Účel: účtovníctvo a dane. Právny základ: zákonná povinnosť, čl. 6 ods. 1 písm. c) GDPR, zákon č. 431/2002 Z. z. Doba uchovávania: 10 rokov.</li>
<li><strong>IP adresa, technické logy</strong> — Účel: bezpečnosť a predchádzanie zneužitiu. Právny základ: oprávnený záujem na bezpečnej prevádzke systému, čl. 6 ods. 1 písm. f) GDPR. Doba uchovávania: {{gdpr.uchovavanie_logov_dni}} dní.</li>
</ul>
<p>Meno a kontakt sú potrebné na uzavretie zmluvy; bez nich objednávku cez QR kód nemožno odoslať. Môžete si objednať priamo u obsluhy.</p>

<h3>3. Príjemcovia</h3>
<ul>
<li><strong>Google (služba Firebase)</strong> – hosting a databáza; údaje sú uložené v dátových centrách v EÚ. Google môže v obmedzenom rozsahu (technická podpora, doručovanie obsahu) pristupovať k údajom aj z krajín mimo EÚ/EHP, najmä z USA. Takýto prenos je chránený rozhodnutím Európskej komisie o primeranosti (EU-US Data Privacy Framework) a štandardnými zmluvnými doložkami.</li>
{{#if d_online}}<li><strong>Besteron a.s.</strong>, Teslova 20a, 821 02 Bratislava, IČO 47 866 233 – platobná brána. Údaje o platbe a karte zadávate priamo jej a spracúva ich ako samostatný prevádzkovateľ podľa svojich zásad ochrany osobných údajov (besteron.com/privacy-policy). Prevádzka ani Datavision údaje o karte nevidia.</li>{{/if}}
<li><strong>Brevo (Sendinblue SAS, Francúzsko – EÚ)</strong> – odosielanie potvrdení a oznamov o stave objednávky.</li>
<li>Orgány verejnej moci, ak to vyžaduje zákon.</li>
</ul>
<p>Automatizované rozhodovanie ani profilovanie nepoužívame.</p>

<h3>4. Cookies</h3>
<p>Stránka používa len nevyhnutné cookies (košík, relácia, jazyk), ktoré nevyžadujú súhlas. Analytické ani marketingové cookies nepoužívame.</p>

<h3>5. Vaše práva</h3>
<p>Máte právo na prístup, opravu, vymazanie, obmedzenie spracúvania, prenosnosť údajov a námietku proti spracúvaniu na základe oprávneného záujmu. Žiadosť pošlite na {{prevadzka.email}} alebo info@datavision.sk; vybavíme ju do 1 mesiaca. Sťažnosť môžete podať Úradu na ochranu osobných údajov SR, Hraničná 12, 820 07 Bratislava (dataprotection.gov.sk).</p>
`.trim();

/** Render the final HTML from the customer variables. */
export const renderTerms = (vars) => applyTemplate(TEMPLATE_HTML, buildContext(vars));
